/**
 * formClasses — the Tailwind class strings the app's modal/CRUD forms share.
 *
 * These three strings had been copied verbatim into four files (the Admin CRUD
 * modal, the CMS Areas tab, the Add Building modal, and from there into the
 * BuildingFieldset). Copies drift: the day someone raises the focus ring or
 * changes the border token, three of the four forms keep the old look and
 * nobody notices until the screenshots sit side by side.
 *
 * This is deliberately NOT a component. Where a form field can be the `Input`
 * atom or the `FormField` molecule, use those — they bring the label, the error
 * text, the aria wiring and the touch floor. These strings are for the forms
 * built directly on `<input>` / `<select>` / `<textarea>`, which is most of the
 * Admin and CMS CRUD modals, and they exist so those forms match each other.
 *
 * Both height and type size follow the house pattern from CONVENTIONS §5:
 * 44px and 16px on touch (16px is what stops iOS Safari zooming on focus),
 * dropping to a denser 36px from `desktop:` up.
 *
 * @example
 * import { FORM_INPUT_CLASS, FORM_LABEL_CLASS } from '../../utils/formClasses';
 *
 * <label htmlFor="name" className={FORM_LABEL_CLASS}>Name</label>
 * <input id="name" className={FORM_INPUT_CLASS} … />
 */

/** Text input / textarea. */
export const FORM_INPUT_CLASS = 'w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary';

/** Select — the input, plus the white ground a native select needs. */
export const FORM_SELECT_CLASS = `${FORM_INPUT_CLASS} bg-white`;

/** The label above either of them. */
export const FORM_LABEL_CLASS = 'block text-14px font-medium text-brand-text mb-4px';

/* ============================================
 * Job form — the work order / inspection section molecules
 * ============================================
 * The section molecules (Assignment, Billing, Settings, …) take their look
 * from four tokens the host passes down. JobForm carried them privately
 * until the assign-from-the-list dialog needed to render the Assignment
 * section too; a second copy would have let the two drift apart.
 *
 * These deliberately differ from FORM_INPUT_CLASS above: the sections keep
 * the 44px floor at every breakpoint (a long form is filled in on a tablet
 * as often as a desk) and split the border into a normal/error pair so a
 * field can turn red without a second class string per input. */

/** Text / date / time input and select inside a section — no border colour. */
export const JOB_FORM_INPUT_CLASS = 'block w-full rounded-md border px-12px py-10px text-16px font-sans min-h-44px focus:outline-none focus:ring-2';

/** Border + focus ring for a field with no validation error. */
export const JOB_FORM_NORMAL_BORDER = 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary';

/** Border + focus ring for a field the validator flagged. */
export const JOB_FORM_ERROR_BORDER = 'border-red-500 focus:ring-red-500';

/** The label above a section field. */
export const JOB_FORM_LABEL_CLASS = 'block text-14px font-sans font-medium text-gray-700 mb-4px';

/** The bundle every section molecule takes as `labelClass` / `inputClass` /
 *  `normalBorder` / `errorBorder` — spread it into the section's props. */
export const JOB_FORM_STYLING = {
	labelClass: JOB_FORM_LABEL_CLASS,
	inputClass: JOB_FORM_INPUT_CLASS,
	normalBorder: JOB_FORM_NORMAL_BORDER,
	errorBorder: JOB_FORM_ERROR_BORDER,
} as const;
